import React, { useState, useRef, useEffect } from 'react'
import dkabioLogo from './images/dkabio-logo-wordmark.jpg'
import haAppLogo from './images/ha-app-logo.jpg'
import healthTwinLogo from './images/healthtwin-initiative-logo.jpg'
import './DkabioSuccessCard.css'

const DKABIO_LINE_URL = 'https://lin.ee/rGWQUQC'
const ALLIANCE_LINE_URL = 'https://lin.ee/Pe4R0t4'

// 在 App 內嵌 WebView 裡 navigator.clipboard 常不可用，退回 execCommand
const copyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (e) {
    // 繼續走 fallback
  }
  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.setAttribute('readonly', '')
  textArea.style.position = 'fixed'
  textArea.style.opacity = '0'
  document.body.appendChild(textArea)
  textArea.select()
  try {
    return document.execCommand('copy')
  } catch (e) {
    return false
  } finally {
    document.body.removeChild(textArea)
  }
}

const LineIcon = () => (
  <svg className="dkabio-success__line-icon" viewBox="0 0 24 24">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
  </svg>
)

const ArrowIcon = () => (
  <svg
    className="dkabio-success__arrow"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 4.5l7.5 7.5-7.5 7.5"
    />
  </svg>
)

const CopyIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
    />
  </svg>
)

// 加入 LINE 按鈕 + 複製連結 + 提示，兩個區塊共用
const LineJoin = ({ url, label, copyLabel, copiedText, variant, pulse }) => {
  const [toast, setToast] = useState('')
  const timerRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const handleCopy = async () => {
    const ok = await copyText(url)
    setToast(ok ? copiedText : `複製失敗，請手動複製：${url}`)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setToast(''), 2400)
  }

  return (
    <>
      <a
        className={`dkabio-success__line-btn dkabio-success__line-btn--${variant}${
          pulse ? ' dkabio-success__line-btn--pulse' : ''
        }`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="dkabio-success__line-left">
          <LineIcon />
          <span className="dkabio-success__line-main">{label}</span>
        </span>
        <ArrowIcon />
      </a>
      <div className="dkabio-success__copy-row">
        <button
          type="button"
          className={`dkabio-success__copy-btn dkabio-success__copy-btn--${variant}`}
          onClick={handleCopy}
        >
          <CopyIcon />
          {copyLabel}
        </button>
      </div>
      <div
        className={`dkabio-success__toast dkabio-success__toast--${variant}`}
        aria-live="polite"
      >
        {toast}
      </div>
    </>
  )
}

const DkabioSuccessCard = () => (
  <main className="dkabio-success">
    <header className="dkabio-success__header">
      <img
        className="dkabio-success__ha-logo"
        src={haAppLogo}
        alt="HA! APP Logo"
      />
      <h1 className="dkabio-success__title">
        HA! 健康
        <br />
        <span>實現精準健康管理</span>
      </h1>
    </header>

    <section className="dkabio-success__section dkabio-success__section--dkabio">
      <h2 className="dkabio-success__heading">
        更多即時資訊與健康內容，
        <br />
        歡迎加入 <strong>DKABio 官方 LINE</strong>
      </h2>
      <div className="dkabio-success__tags">
        <span className="dkabio-success__tag dkabio-success__tag--emerald">
          💚 衛教新知
        </span>
        <span className="dkabio-success__tag dkabio-success__tag--amber">
          💡 APP操作指南
        </span>
        <span className="dkabio-success__tag dkabio-success__tag--rose">
          🎁 最新產品資訊
        </span>
      </div>
      <LineJoin
        url={DKABIO_LINE_URL}
        label="點擊加入 DKABio 好友"
        copyLabel="複製 DKABio LINE 連結"
        copiedText="✓ 已成功複製 DKABio 連結！"
        variant="line"
        pulse
      />
    </section>

    <section className="dkabio-success__section dkabio-success__section--alliance">
      <div className="dkabio-success__badge">
        <span>🏢</span> 企業用戶專區
      </div>
      <h2 className="dkabio-success__heading">
        《健康孿生倡議聯盟》邀您一同打造健康職場！
      </h2>
      <div className="dkabio-success__tags">
        <span className="dkabio-success__tag dkabio-success__tag--teal">
          ✨ 企業會員專屬福利
        </span>
        <span className="dkabio-success__tag dkabio-success__tag--emerald">
          🌱 聯盟最新消息
        </span>
      </div>
      <LineJoin
        url={ALLIANCE_LINE_URL}
        label="點擊加入聯盟好友"
        copyLabel="複製聯盟 LINE 連結"
        copiedText="✓ 已成功複製聯盟連結！"
        variant="alliance"
      />
    </section>

    <footer className="dkabio-success__footer">
      <div className="dkabio-success__logos">
        <img
          className="dkabio-success__dkabio-logo"
          src={dkabioLogo}
          alt="DKABio Logo"
        />
        <span className="dkabio-success__times">✕</span>
        <img
          className="dkabio-success__alliance-logo"
          src={healthTwinLogo}
          alt="健康孿生倡議聯盟 HealthTwin Initiative"
        />
      </div>
      <p className="dkabio-success__company">浚鴻數據開發股份有限公司</p>
    </footer>
  </main>
)

export default DkabioSuccessCard
